import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import store from './store/store';
import AppRoutes from './routes';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { DemoProvider } from './contexts/DemoContext';
import { EntityProvider } from './contexts/EntityContext';
import { ThemeModeContext } from './contexts/ThemeModeContext';
import MobileNotice from './components/common/MobileNotice';
import userService from './services/userService';
import { warmPages } from './utils/lazyPage';

// Dark/light palette values — mirror the CSS variables in index.css so MUI-styled
// components and CSS-variable-styled components stay visually consistent.
const PALETTE = {
  light: {
    background: { default: '#FAFAFA', paper: '#FFFFFF' },
    text: { primary: '#1E293B', secondary: '#64748B' },
    divider: '#E5E7EB'
  },
  dark: {
    background: { default: '#0B0F19', paper: '#111827' },
    text: { primary: '#F1F5F9', secondary: '#94A3B8' },
    divider: '#1E293B'
  }
};

// Create the MUI theme for a given mode. Previously this was a fixed light-only
// constant, so every MUI-default-background component (Paper/Card/Dialog/Menu/…)
// stayed white in dark mode. Now palette.mode + background/text/divider switch with
// the toggle, so MUI components adapt natively.
const createAppTheme = (mode = 'light') => createTheme({
  palette: {
    // Colour budget (3 Oct 2026): the library's default blue and purple were
    // leaking into every `color="primary"` button, chip and link. The one
    // accent is the brand teal; the semantic colours match the CSS tokens.
    primary: mode === 'dark'
      ? { main: '#2DD4BF', light: '#5EEAD4', dark: '#14B8A6', contrastText: '#0B0F14' }
      : { main: '#0F766E', light: '#14B8A6', dark: '#115E59', contrastText: '#FFFFFF' },
    secondary: mode === 'dark'
      ? { main: '#94A3B8', light: '#CBD5E1', dark: '#64748B' }
      : { main: '#64748B', light: '#94A3B8', dark: '#475569' },
    success: mode === 'dark' ? { main: '#34D399' } : { main: '#15803D' },
    error: mode === 'dark' ? { main: '#F87171' } : { main: '#B91C1C' },
    warning: mode === 'dark' ? { main: '#FBBF24' } : { main: '#B45309' },
    info: mode === 'dark' ? { main: '#94A3B8' } : { main: '#64748B' },
    mode,
    background: PALETTE[mode].background,
    text: PALETTE[mode].text,
    divider: PALETTE[mode].divider
  },
  typography: {
    fontFamily: '"IBM Plex Sans", -apple-system, "Segoe UI", sans-serif',
    fontSize: 14,
    body1: {
      fontSize: '0.875rem',  // 14px
      fontWeight: 400,
      color: 'var(--text-primary)'
    },
    body2: {
      fontSize: '0.8125rem', // 13px
      fontWeight: 400,
      color: 'var(--text-secondary)'
    },
    h4: {
      fontSize: '2.25rem',   // 36px — card headline numbers
      fontWeight: 600,
      color: 'var(--text-primary)'
    },
    h5: {
      fontSize: '1.25rem',   // 20px
      fontWeight: 600,
      color: 'var(--text-primary)'
    },
    h6: {
      fontSize: '1rem',      // 16px — page titles
      fontWeight: 600,
      color: 'var(--text-primary)'
    },
    subtitle1: {
      fontSize: '0.875rem',  // 14px
      fontWeight: 600,
      color: 'var(--text-primary)'
    },
    subtitle2: {
      fontSize: '0.8125rem', // 13px
      fontWeight: 600,
      color: 'var(--text-primary)'
    },
    caption: {
      fontSize: '0.6875rem', // 11px
      fontWeight: 400,
      color: 'var(--text-muted)'
    }
  },
  components: {
    // ── Base ──────────────────────────────────────────────
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: 'var(--bg-page)',
          color: 'var(--text-primary)',
          // Figures line up in columns everywhere (3 Oct 2026): a 1 is as
          // wide as a 7, so $1,234.56 sits under $7,890.12 digit for digit.
          fontVariantNumeric: 'tabular-nums'
        }
      }
    },

    // ── Cards ─────────────────────────────────────────────
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: 'var(--bg-card)',
          border: '1px solid var(--border-card)',
          boxShadow: 'var(--shadow-card)',
          transition: 'box-shadow 0.2s ease',
          '&:hover': {
            boxShadow: 'var(--shadow-card-hover)'
          }
        }
      }
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          color: 'var(--text-primary)',
          '& .MuiTypography-body2': {
            color: 'var(--text-secondary)'
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--bg-card)',
          backgroundImage: 'none'
        }
      }
    },

    // ── Tables (3A) ───────────────────────────────────────
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--bg-card)'
        }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: 'var(--bg-table-header)',
            color: 'var(--text-muted)',
            fontSize: '0.6875rem',
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            borderBottom: '1px solid var(--border)',
            whiteSpace: 'nowrap'
          }
        }
      }
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            backgroundColor: 'var(--bg-card)',
            '&:hover': {
              backgroundColor: 'var(--bg-table-hover) !important'
            }
          },
          '& .MuiTableCell-body': {
            color: 'var(--text-primary)',
            borderBottom: '1px solid var(--border)',
            fontSize: '0.84375rem'  // 13.5px
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid var(--border)'
        }
      }
    },

    // ── Forms & Inputs (3B) ───────────────────────────────
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--bg-input)',
          color: 'var(--text-primary)',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--border)'
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--teal)'
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--teal)'
          }
        },
        input: {
          '&::placeholder': {
            color: 'var(--text-muted)',
            opacity: 1
          }
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'var(--text-secondary)',
          fontWeight: 500,
          '&.Mui-focused': {
            color: 'var(--teal)'
          }
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: 'var(--text-primary)'
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: 'var(--text-secondary)'
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: 'var(--text-muted)'
        }
      }
    },

    // Toggle groups (None / Type / Account, None / Product / Direction) were
    // the library's default ALL CAPS beside sentence-case buttons (3 Oct 2026).
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          '&.Mui-selected': {
            color: 'var(--teal)'
          }
        }
      }
    },

    // ── Buttons (3C) ──────────────────────────────────────
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8
        },
        containedPrimary: {
          backgroundColor: 'var(--teal)',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: 'var(--teal-hover)'
          }
        },
        outlined: {
          backgroundColor: 'transparent',
          borderColor: 'var(--border)',
          color: 'var(--text-primary)',
          '&:hover': {
            backgroundColor: 'var(--bg-table-hover)',
            borderColor: 'var(--border)'
          }
        }
      }
    },

    // ── Dialogs / Modals (3D) ─────────────────────────────
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: 'var(--bg-card-elevated)',
          border: '1px solid var(--border-card)',
          boxShadow: 'var(--shadow-card-hover)'
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: 'var(--text-primary)'
        }
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          color: 'var(--text-secondary)'
        }
      }
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          color: 'var(--text-secondary)'
        }
      }
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }
      }
    },

    // ── Dropdowns & Menus (3E) ────────────────────────────
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: 'var(--bg-card-elevated) !important',
          border: '1px solid var(--border)',
          boxShadow: 'var(--shadow-card) !important'
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: 'var(--text-primary)',
          '&:hover': {
            backgroundColor: 'var(--bg-table-hover)'
          },
          '&.Mui-selected': {
            backgroundColor: 'var(--bg-sidebar-active)',
            color: 'var(--teal)',
            '&:hover': {
              backgroundColor: 'var(--bg-sidebar-active)'
            }
          }
        }
      }
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: 'var(--bg-card-elevated)',
          border: '1px solid var(--border)',
          boxShadow: 'var(--shadow-card)'
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          backgroundColor: 'var(--bg-card-elevated)',
          border: '1px solid var(--border)'
        },
        option: {
          '&:hover': {
            backgroundColor: 'var(--bg-table-hover) !important'
          },
          '&[aria-selected="true"]': {
            backgroundColor: 'var(--bg-sidebar-active) !important'
          }
        }
      }
    },

    // ── Tooltips (3F) ─────────────────────────────────────
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'var(--bg-card-elevated)',
          color: 'var(--text-primary)',
          border: '1px solid var(--border)',
          fontSize: '0.75rem',
          boxShadow: 'var(--shadow-card)'
        },
        arrow: {
          color: 'var(--bg-card-elevated)',
          '&::before': {
            border: '1px solid var(--border)'
          }
        }
      }
    },

    // ── Chips / Tags (3G) ─────────────────────────────────
    MuiChip: {
      styleOverrides: {
        root: {
          borderColor: 'var(--border)',
          fontWeight: 500
        },
        outlined: {
          borderColor: 'var(--border)',
          color: 'var(--text-primary)'
        },
        // Colour budget (3 Oct 2026): a coloured chip is a soft tint with the
        // colour as text, never a solid block with white on it. One rule for
        // every status chip in the app.
        filledPrimary: { backgroundColor: 'var(--teal-dim)', color: 'var(--teal)' },
        filledSuccess: { backgroundColor: 'var(--green-bg)', color: 'var(--green)' },
        filledError: { backgroundColor: 'var(--red-bg)', color: 'var(--red)' },
        filledWarning: { backgroundColor: 'var(--orange-bg)', color: 'var(--orange)' },
        filledInfo: { backgroundColor: 'var(--bg-table-header)', color: 'var(--text-secondary)' },
        filledDefault: { backgroundColor: 'var(--bg-table-header)', color: 'var(--text-secondary)' }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        // Row actions rest grey; the colour shows on hover.
        colorPrimary: { color: 'var(--text-secondary)', '&:hover': { color: 'var(--teal)' } },
        colorSuccess: { color: 'var(--text-secondary)', '&:hover': { color: 'var(--green)' } },
        colorWarning: { color: 'var(--text-secondary)', '&:hover': { color: 'var(--orange)' } },
        colorError: { color: 'var(--text-secondary)', '&:hover': { color: 'var(--red)' } },
        colorInfo: { color: 'var(--text-secondary)', '&:hover': { color: 'var(--text-primary)' } }
      }
    },

    // ── Misc shared components ────────────────────────────
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'var(--border)'
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: 'var(--text-secondary)'
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: 'var(--text-secondary)',
          textTransform: 'none',
          '&.Mui-selected': {
            color: 'var(--teal)'
          }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: 'var(--teal)'
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: 'var(--teal)',
            '& + .MuiSwitch-track': {
              backgroundColor: 'var(--teal)'
            }
          }
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: 'var(--text-muted)',
          '&.Mui-checked': {
            color: 'var(--teal)'
          }
        }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: 'var(--text-muted)',
          '&.Mui-checked': {
            color: 'var(--teal)'
          }
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8
        },
        standardSuccess: {
          backgroundColor: 'var(--green-bg)',
          color: 'var(--green)'
        },
        standardError: {
          backgroundColor: 'var(--red-bg)',
          color: 'var(--red)'
        },
        standardWarning: {
          backgroundColor: 'var(--orange-bg)',
          color: 'var(--orange)'
        },
        // Colour budget (3 Oct 2026): information is not a state, so it is
        // not blue. Neutral ground, text colour, grey icon.
        standardInfo: {
          backgroundColor: 'var(--bg-table-header)',
          color: 'var(--text-primary)',
          border: '1px solid var(--border-card)',
          '& .MuiAlert-icon': { color: 'var(--text-secondary)' }
        }
      }
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          '& .MuiStepLabel-label': {
            color: 'var(--text-secondary)',
            '&.Mui-active': {
              color: 'var(--teal)'
            },
            '&.Mui-completed': {
              color: 'var(--green)'
            }
          },
          '& .MuiStepIcon-root': {
            color: 'var(--border)',
            '&.Mui-active': {
              color: 'var(--teal)'
            },
            '&.Mui-completed': {
              color: 'var(--green)'
            }
          }
        }
      }
    },
    MuiPagination: {
      styleOverrides: {
        root: {
          '& .MuiPaginationItem-root': {
            color: 'var(--text-secondary)',
            borderColor: 'var(--border)',
            '&.Mui-selected': {
              backgroundColor: 'var(--teal)',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: 'var(--teal-hover)'
              }
            }
          }
        }
      }
    }
  }
});

function App() {
  // Single source of truth for light/dark. Drives BOTH the MUI theme palette
  // (so MUI-default-background components adapt) AND the CSS `data-theme`
  // attribute (so the CSS-variable-styled components adapt) — previously only the
  // latter switched, leaving every MUI Paper/Card/Dialog white in dark mode.
  const [mode, setMode] = useState(() => localStorage.getItem('nvst-theme') || 'light');

  // Write data-theme SYNCHRONOUSLY during render (not in an effect) so it is on
  // the DOM before any child renders. Children that read CSS variables at render
  // time — chart theming via getComputedStyle, etc. — would otherwise see the
  // previous theme for one tick (effects run after children render), leaving them
  // a toggle behind. Setting an attribute idempotently in render is safe.
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', mode);
  }

  useEffect(() => {
    localStorage.setItem('nvst-theme', mode);
  }, [mode]);

  // A "setup finished" flag that could not be confirmed last time is retried
  // quietly once per app load (see userService.ensureOnboardingComplete).
  useEffect(() => { userService.retryPendingOnboardingComplete(); }, []);

  // Fetch every page's code in the background shortly after the first draw,
  // so the first click on any page is instant (see warmPages).
  useEffect(() => { warmPages(); }, []);

  const toggleMode = useCallback(() => setMode((m) => (m === 'light' ? 'dark' : 'light')), []);
  const theme = useMemo(() => createAppTheme(mode), [mode]);
  const themeModeValue = useMemo(() => ({ mode, toggleMode }), [mode, toggleMode]);

  return (
    <Provider store={store}>
      <ThemeModeContext.Provider value={themeModeValue}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {/* MobileNotice renders nothing on viewports ≥ 768px. It mounts
              outside BrowserRouter so it shows on every route (including
              auth pages) and doesn't depend on router context. */}
          <MobileNotice />
          <BrowserRouter>
            <CurrencyProvider>
              <DemoProvider>
                <EntityProvider>
                  <AppRoutes />
                </EntityProvider>
              </DemoProvider>
            </CurrencyProvider>
          </BrowserRouter>
        </ThemeProvider>
      </ThemeModeContext.Provider>
    </Provider>
  );
}

export default App;
